import type { DashboardSpec, QueryBinding } from "../dashboard/catalog"
import type { Participant, ParticipantRole } from "@/services/project-service"

import { api } from "@/lib/api-client"
import { logger } from "@/lib/logger"

// Re-export sharing types so dash callers don't have to reach into
// project-service.ts directly. Same shape — sharing is uniform across
// the app and we want a single source of truth.
export type { Participant, ParticipantRole } from "@/services/project-service"

export type DashboardParticipantsResponse = {
  participants: Participant[]
  currentUserRole: ParticipantRole
  ownerUserId: string
}

export type DashboardInvitationNotification = {
  invitedBy: string
  invitedByDisplayName: string
  role: ParticipantRole
}

export type QualityReviewIssue = {
  severity: "high" | "medium" | "low"
  category: string
  message: string
  element_path?: string | null
  suggested_fix?: string | null
}

export type QualityReview = {
  score: number
  summary: string
  issues: QualityReviewIssue[]
  reviewed_model?: string | null
  reviewed_at?: string | null
  suggested_patch?: Array<Record<string, unknown>>
}

export type QualityHistoryEntry = {
  score: number
  summary: string
  reviewed_at?: string | null
  reviewed_model?: string | null
}

export type SavedDashboard = {
  id: string
  title: string
  description: string
  spec: Record<string, unknown>
  type: "personal" | "company"
  is_favorite?: boolean
  update_on_load?: boolean
  query_bindings?: QueryBinding[]
  /** Latest AI quality review (null until reviewed). */
  quality_review?: QualityReview | null
  /** Append-only history of slim review snapshots for the sparkline. */
  quality_history?: QualityHistoryEntry[]
  created: string
  updated: string
  // Sharing fields. Present on responses that pass through the new
  // `_doc_to_response` path; older cached responses (or company
  // dashboards) may omit them.
  ownerUserId?: string
  participants?: Participant[]
  currentUserRole?: ParticipantRole
  hasUnviewedNotification?: boolean
  invitationNotification?: DashboardInvitationNotification | null
}

export type RefreshResult = SavedDashboard & {
  refresh_results: {
    total: number
    succeeded: number
    failed: number
    errors: Array<{ binding_id: string; error: string }>
  }
}

export type CreateDashboardPayload = {
  title: string
  description?: string
  spec: Record<string, unknown> | DashboardSpec
  type?: "personal" | "company"
  update_on_load?: boolean
}

export type UpdateDashboardPayload = {
  title?: string
  description?: string
  spec?: Record<string, unknown> | DashboardSpec
  type?: "personal" | "company"
  update_on_load?: boolean
  /** Explicit visual edits may replace/rebase the canonical binding envelope. */
  replace_query_bindings?: boolean
}

export const DashboardService = {
  async listDashboards(): Promise<SavedDashboard[]> {
    logger.debug("DashboardService.listDashboards")
    const data = await api.get<never, { dashboards: SavedDashboard[] }>("/dashboards")
    return data.dashboards
  },

  async getDashboard(dashboardId: string): Promise<SavedDashboard> {
    logger.debug("DashboardService.getDashboard", { dashboardId })
    return api.get<never, SavedDashboard>(`/dashboards/${dashboardId}`)
  },

  async createDashboard(payload: CreateDashboardPayload): Promise<SavedDashboard> {
    logger.debug("DashboardService.createDashboard", { title: payload.title })
    return api.post<never, SavedDashboard>("/dashboards", payload)
  },

  async updateDashboard(
    dashboardId: string,
    payload: UpdateDashboardPayload
  ): Promise<SavedDashboard> {
    logger.debug("DashboardService.updateDashboard", { dashboardId })
    return api.put<never, SavedDashboard>(`/dashboards/${dashboardId}`, payload)
  },

  async toggleFavorite(dashboardId: string): Promise<SavedDashboard> {
    logger.debug("DashboardService.toggleFavorite", { dashboardId })
    return api.patch<never, SavedDashboard>(`/dashboards/${dashboardId}/favorite`)
  },

  async deleteDashboard(
    dashboardId: string,
    type: "personal" | "company" = "personal"
  ): Promise<void> {
    logger.debug("DashboardService.deleteDashboard", { dashboardId })
    await api.delete<never, void>(`/dashboards/${dashboardId}`, { params: { type } })
  },

  // -------- Sharing --------

  async listParticipants(dashboardId: string): Promise<DashboardParticipantsResponse> {
    logger.debug("DashboardService.listParticipants", { dashboardId })
    return api.get<never, DashboardParticipantsResponse>(`/dashboards/${dashboardId}/participants`)
  },

  async addParticipant(
    dashboardId: string,
    participant: {
      oid: string
      displayName: string
      type: "user" | "group"
      role: ParticipantRole
    }
  ): Promise<{ success: boolean; participant: Participant }> {
    logger.debug("DashboardService.addParticipant", { dashboardId, oid: participant.oid })
    return api.post<typeof participant, { success: boolean; participant: Participant }>(
      `/dashboards/${dashboardId}/participants`,
      participant
    )
  },

  async updateParticipantRole(
    dashboardId: string,
    participantOid: string,
    role: ParticipantRole
  ): Promise<{ success: boolean; role: ParticipantRole }> {
    logger.debug("DashboardService.updateParticipantRole", {
      dashboardId,
      participantOid,
      role,
    })
    return api.put<{ role: ParticipantRole }, { success: boolean; role: ParticipantRole }>(
      `/dashboards/${dashboardId}/participants/${participantOid}`,
      { role }
    )
  },

  async removeParticipant(
    dashboardId: string,
    participantOid: string
  ): Promise<{ success: boolean }> {
    logger.debug("DashboardService.removeParticipant", { dashboardId, participantOid })
    return api.delete<never, { success: boolean }>(
      `/dashboards/${dashboardId}/participants/${participantOid}`
    )
  },

  async markViewed(dashboardId: string): Promise<{ success: boolean }> {
    logger.debug("DashboardService.markViewed", { dashboardId })
    return api.post<never, { success: boolean }>(
      `/dashboards/${dashboardId}/mark-viewed`,
      undefined
    )
  },

  async refreshDashboard(
    dashboardId: string,
    state?: Record<string, unknown>
  ): Promise<RefreshResult | null> {
    logger.debug("DashboardService.refreshDashboard", {
      dashboardId,
      hasState: state !== undefined,
    })
    // Send the current dashboard state so the backend can resolve any
    // params_from_state placeholders in stored bindings. Omit the key
    // entirely when the caller didn't provide one so older backends
    // continue to receive the exact same empty body shape.
    const body = state ? { state } : {}
    const response = await api.post<typeof body, RefreshResult | "">(
      `/dashboards/${dashboardId}/refresh`,
      body
    )
    // 204 No Content — no query bindings, use AI refresh instead
    if (response === "") {
      return null
    }
    return response
  },
}
